import { useTranslation } from 'react-i18next';

import { Input, Pagination, Spin } from '@arco-design/web-react';

import { EmptyTips } from '@/components';
import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { RepoSelectorCard } from '@/components-biz/RepoSelectorCard';

import { type AppRepoImageQuery, useAppRepoImageList } from './hooks/useAppRepoImageList';
import styles from './index.module.less';

export { useAppRepoImageList } from './hooks/useAppRepoImageList';
export type { AppRepoImageQuery } from './hooks/useAppRepoImageList';

export interface AppRepoImageSelectorProps {
  disabled?: boolean;
  queryObj?: AppRepoImageQuery;
  /** 当前选中的镜像 id（imageKey） */
  value?: string;
  onChange?: (item: CommonImageItemType) => void;
}

/** 应用仓库容器镜像选择（创建应用实例弹窗用） */
export function AppRepoImageSelector({ disabled = false, queryObj, value, onChange }: AppRepoImageSelectorProps) {
  const { t } = useTranslation();
  const { items, loading, page, limit, total, inputValue, onInputChange, onSearch, onPageChange } = useAppRepoImageList(queryObj);

  const handleSelect = (item: CommonImageItemType) => {
    if (disabled) return;
    const key = String(item.id ?? item.key ?? '').trim();
    if (!key) return;
    onChange?.({ ...item, key });
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchBar}>
        <h5 className={styles.title}>{t('hooks:ai.app')}</h5>
        <Input.Search
          placeholder={t('data-center:maas.app.repo.search')}
          allowClear
          value={inputValue}
          onChange={onInputChange}
          onSearch={onSearch}
          onClear={() => onSearch('')}
          disabled={disabled}
          style={{ width: '40%' }}
        />
      </div>

      <Spin loading={loading} block>
        <div className={styles.repoGrid}>
          {items.map((item) => {
            const key = String(item.id ?? item.key ?? '').trim();
            const selected = !!value && value === key;
            return (
              <RepoSelectorCard
                key={key}
                name={item.name}
                metaItems={[item.vendor, item.version, item.cpuArchitectures?.length ? item.cpuArchitectures.join(', ') : undefined]}
                address={item.dockerRefUrl}
                selected={selected}
                disabled={disabled}
                onClick={() => handleSelect(item)}
              />
            );
          })}
        </div>

        {!loading && items.length === 0 ? (
          <div className={styles.emptyWrapper}>
            <EmptyTips tips={t('data-center:maas.app.repo.empty')} />
          </div>
        ) : null}

        {total > limit ? (
          <div className={styles.paginationWrapper}>
            <Pagination size="mini" current={page} pageSize={limit} total={total} onChange={onPageChange} showTotal disabled={disabled} />
          </div>
        ) : null}
      </Spin>
    </div>
  );
}
